    import {
    Injectable,
    signal,
    computed,
    inject,
    effect,
    } from '@angular/core';

    import { defer } from 'rxjs';
    import { Router, ActivatedRoute } from '@angular/router';
    import { findRouteWithParam } from '../helpers/utils';
    import { toSignal } from '@angular/core/rxjs-interop';
    import { NavigationEnd } from '@angular/router';

    import { toObservable } from '@angular/core/rxjs-interop';
    import { debounceTime, distinctUntilChanged, map,  filter,  startWith, switchMap } from 'rxjs/operators';
    import { HuntService } from '../../../modules/shared/services/hunt.service';
    import { deepMerge, DeepPartial, Enums, Hunt, Environment } from '@cyborg-monorepo/hunter/common';
    import { BadgeVariant } from '@hyperkit';
    import { GeneratedContent } from '@cyborg-monorepo/hunter/common';
    import { HuntStateService } from './hunt-state.service';

    type SavingStatus = 'idle' | 'saving' | 'error' | 'success';
    import { Subject } from 'rxjs';
    @Injectable({ providedIn: 'root' })
    export class HuntEditorStoreService {
    private readonly huntService = inject(HuntService);
    private readonly huntStateService = inject(HuntStateService); // ✅ inject here
    private readonly _original = signal<Hunt | null>(null);
    private readonly _hunt = signal<Hunt | null>(null);
    private readonly rootRoute = inject(ActivatedRoute); // ✅ inject once at top

    
    // 👉 ADD this
    // private readonly queryLogic = inject(QueryActiveHuntPackageQueryService);
  

    private readonly _triggerUpdate$ = new Subject<void>();

    // ✅ Public observable for components to subscribe to
    readonly triggerUpdate$ = this._triggerUpdate$.asObservable();
  
    // ✅ Method to emit the trigger
    triggerManualUpdate(): void {
      this._triggerUpdate$.next();
    }
  

    // 🔐 Internal reactive state
    private readonly _selectedEnvironmentIds = signal<string[]>([]);
    private readonly _toolSelections = signal<string[]>([]);

        // ✅ Public accessors
    getSelectedEnvironmentIds(): string[] {
      return this._selectedEnvironmentIds();
    }

    getToolSelections(): string[] {
      return this._toolSelections();
    }
    // ✅ Manual rehydration to trigger computed updates downstream
    rehydrate(): void {
      const oldEnv = this.selectedEnvironment();
      const newEnv = this.activePackage()?.analysis.find(e => e.id === oldEnv?.id);
    
      if (newEnv) {
        this.selectedEnvironment.set({ ...newEnv }); // 🔁 force tool update
      }
    
      this._selectedEnvironmentIds.set([...this._selectedEnvironmentIds()]);
      this._toolSelections.set([...this._toolSelections()]);
    
    }


    private readonly _savingStatus = signal<SavingStatus>('idle');
    readonly savingStatus = computed(() => this._savingStatus());

    private readonly _undoStack = signal<Hunt[]>([]);
    private readonly _redoStack = signal<Hunt[]>([]);

    readonly hunt = computed(() => this._hunt());
    readonly isDirty = computed(() =>
        JSON.stringify(this._hunt()) !== JSON.stringify(this._original())
    );

    readonly draftSavedAt = signal<number | null>(null);

    

    private readonly _selectedPackageId = signal<string | null>(null);

    readonly selectedPackageId = computed(() =>
      this._selectedPackageId() ?? this.packageId()
    );

    setSelectedPackageId(id: string | null) {
      this._selectedPackageId.set(id);
    }

    
    
    // MOVING ENV STUFF
    readonly selectedEnvironment = signal<Environment | null>(null);
    readonly getAllEnvironments = computed(() => this.activePackage()?.analysis ?? []);
    readonly getNumEnvironments = computed(() => this.activePackage()?.analysis.length ?? 0);
    readonly getToolsBySelectedEnvironment = computed(() => this.selectedEnvironment()?.tools ?? []);
    readonly getAllTools = computed(() =>
      this.activePackage()?.analysis?.flatMap(env => env.tools ?? []) ?? []
    );




    readonly packageId = toSignal(
      defer(() => {
        const router = inject(Router);
        const initial = findRouteWithParam(this.rootRoute, 'packageId')?.snapshot.paramMap.get('packageId') ?? null;
    
        return router.events.pipe(
          filter(e => e instanceof NavigationEnd),
          startWith(null),
          switchMap(() => {
            const found = findRouteWithParam(this.rootRoute, 'packageId');
            return found?.paramMap ?? this.rootRoute.paramMap;
          }),
          map(params => params.get('packageId')),
          startWith(initial)
        );
      }),
      { initialValue: null }
    );
        

    
    
    setEnvironment(env: Environment) {
      this.selectedEnvironment.set(env);
    }
    

readonly huntPackages = computed(() => this.hunt()?.huntPackages ?? []);

readonly huntPackageTitles = computed(() =>
  this.huntPackages().map(pkg => pkg.title)
);

clearActivePackage(): void {
  const router = inject(Router);
  const route = findRouteWithParam(router.routerState.root, 'packageId');
  if (route) {
    router.navigate(['../'], { relativeTo: route });
  } else {
    console.warn('Could not resolve route to clear package');
  }
}

readonly activePackage = computed(() => {
  const hunt = this.hunt();
  const id = this.selectedPackageId();

  if (!hunt || !hunt.huntPackages || !id) {
    if (!hunt) console.warn('❗ Hunt is not yet loaded in editor');
    if (!id) console.warn('❗ selectedPackageId not set in editor');
    return null;
  }

  const match = hunt.huntPackages.find(pkg => pkg.UUID === id);
  if (!match) console.warn('❌ No matching package found in editor for ID:', id);
  return match ?? null;
});

readonly isHydrated = computed(() => !!this.hunt() && !!this.activePackage());

isActivePackage = (pkgId: string): boolean => {
  return this.selectedPackageId() === pkgId;
};
huntPackageById = (id: string) => {
  return computed(() =>
    this.huntPackages().find(pkg => pkg.UUID === id) ?? null
  );
};

  getAnalysis = (id: string) => computed(() => this.huntPackageById(id)()?.analysis ?? []);

  allTools = (id: string) => computed(() => this.getAnalysis(id)().flatMap(env => env.tools ?? []));

    getTool = (pkgId: string, toolId: string) => computed(() => this.allTools(pkgId)().find(tool => tool.id === toolId) ?? null);


    // TESTING REMOVE
    readonly name = computed(() => this._hunt()?.name ?? '');
    readonly abstract = computed(() => this._hunt()?.abstract ?? 'N/A');
    readonly hypothesis = computed(() => this._hunt()?.hypothesis ?? 'N/A');
    readonly notes = computed(() => this._hunt()?.notes ?? '');
    readonly status = computed(() => this._hunt()?.status ?? Enums.StatusEnum.TO_DO);
    readonly editing = computed(() => this._hunt()?.editing ?? Enums.StatusEnum.TO_DO);
    readonly executiveSummary = computed(() => this._hunt()?.executiveSummary ?? 'N/A');
    readonly outcomeDependent = computed(() => this._hunt()?.outcomeDependent ?? '');
    readonly outcome = computed(() => this._hunt()?.outcome ?? null);
    readonly priority = computed(() => this._hunt()?.priority ?? Enums.PriorityEnum.LOW);

    readonly priorityVariant = computed<BadgeVariant>(() => {
        console.log();
      const priority = this.priority();
      switch (priority) {
        case Enums.PriorityEnum.LOW: return 'success';
        case Enums.PriorityEnum.MEDIUM: return 'warning';
        case Enums.PriorityEnum.HIGH: return 'error';
        default: return 'neutral';
      }
    });

    readonly outcomeVariant = computed<BadgeVariant>(() => {
        const outcome = this.outcome();
        switch (outcome) {
          case Enums.OutcomeEnum.NO_FINDINGS: return 'success';
          case Enums.OutcomeEnum.VISIBILITY_GAP: return 'warning';
          case Enums.OutcomeEnum.DETECTION: return 'information';
          case Enums.OutcomeEnum.ESCALATED: return 'error';
          default: return 'neutral';
        }
      });

      readonly statusVariant = computed<BadgeVariant>(() => {
        const status = this.status();
        switch (status) {
          case Enums.StatusEnum.TO_DO: return 'warning';
          case Enums.StatusEnum.IN_PROGRESS: return 'information';
          case Enums.StatusEnum.DONE: return 'success';
          default: return 'neutral';
        }
      });

     readonly isReportOverviewComplete = computed(() =>
        (this._hunt()?.executiveSummary !== null && this._hunt()?.executiveSummary !== '') &&
      (this._hunt()?.hypothesis !== null && this._hunt()?.hypothesis !== '') &&
      (this._hunt()?.abstract !== null && this._hunt()?.abstract !== '')
      );

      readonly reportOverviewStatus = computed(() => {
        console.log('editor store reportOverviewStatus');

        const summary = this._hunt()?.executiveSummary;
        const hypothesis = this._hunt()?.hypothesis;
        const abstract = this._hunt()?.abstract;
      
        const filledFields = [summary, hypothesis, abstract].filter(value => value !== null && value !== '').length;
      
        console.log("filled fields" + filledFields);
        if (filledFields === 0) {
          return Enums.StatusEnum.TO_DO;
        } else if (filledFields === 3) {
            return Enums.StatusEnum.DONE;
        } else {
            return Enums.StatusEnum.IN_PROGRESS;
        }
      });

  readonly outcomeDependentField = computed(() => {
    return this.outcomeMap.get(this._hunt()?.outcome)}
  );

  public outcomeMap = new Map<string, string>([
    [Enums.OutcomeEnum.DETECTION, 'Suggested Detection'],
    [Enums.OutcomeEnum.NO_FINDINGS, 'Hunt Methodology'],
    [Enums.OutcomeEnum.ESCALATED, 'Escalation Details'],
    [
      Enums.OutcomeEnum.VISIBILITY_GAP,
      'Hunt Limitations/Visibility Improvements',
    ],
  ]);


    constructor() {
        // 🔁 Debounced auto-save to localStorage
        toObservable(this._hunt)
        .pipe(
            debounceTime(1000),
            map((hunt) => JSON.stringify(hunt)),
            distinctUntilChanged() // avoid saving same content twice
        )
        .subscribe((serialized) => {
            const hunt = this._hunt();
            if (!hunt?.id) return;
            localStorage.setItem(`hunt-draft-${hunt.id}`, serialized);
            this.draftSavedAt.set(Date.now());
        });
        effect(() => {
            // console.log('🧠 Current hunt.name in store:', this._hunt()?.name);
          });
          
    }
    readonly content = computed(() => this.activePackage()?.content);
    readonly title = computed(() => this.activePackage()?.title ?? 'Untitled');
    readonly author = computed(() => this.activePackage()?.author ?? 'Unknown');
    readonly description = computed(() => this.activePackage()?.description ?? 'N/A');
    readonly validation = computed(() => this.activePackage()?.validation ?? 'N/A');
    readonly completedDate = computed(() => this.activePackage()?.completed_date);
    readonly UUID = computed(() => this.activePackage()?.UUID ?? null);
    readonly createdDate = computed(() => this.activePackage()?.created_date);
    readonly lastUpdatedDate = computed(() => this.activePackage()?.last_updated);
    readonly severity = computed(() => this.activePackage()?.severity ?? 'N/A');
    
    readonly severityVariant = computed<BadgeVariant>(() => {
      const severity = this.severity();
      switch (severity) {
        case Enums.SeverityEnum.LOW: return 'success';
        case Enums.SeverityEnum.MEDIUM: return 'warning';
        case Enums.SeverityEnum.HIGH: return 'error';
        case Enums.SeverityEnum.CRITICAL: return 'error';
        default: return 'neutral';
      }
    });
    
    readonly queryTable = computed(() => this.activePackage()?.query_logic?.table ?? []);
    readonly threatNames = computed(() => this.activePackage()?.context?.threat_names ?? []);
    readonly threatCategories = computed(() => this.activePackage()?.context?.threat_categories ?? []);
    readonly sourceCountries = computed(() => this.activePackage()?.context?.source_countries.join(', ') ?? []);
    readonly actors = computed(() => this.activePackage()?.context?.actors ?? 'N/A');
    readonly targetOses = computed(() => this.activePackage()?.context?.target_oses.join(', ') ?? []);
    
    readonly dependencies = computed(() => this.activePackage()?.dependencies ?? []);
    readonly diamondModel = computed(() => this.activePackage()?.mapping?.diamond_model ?? 'N/A');
    readonly killChains = computed(() => this.activePackage()?.mapping?.kill_chains.join(', ') ?? []);
    readonly mitreAttackTactics = computed(() => this.activePackage()?.mapping?.mitre?.mitre_attack_tactics.join(', ') ?? []);
    readonly mitreAttackTechniques = computed(() => this.activePackage()?.mapping?.mitre?.mitre_attack_techniques.join(', ') ?? []);
    
    readonly tags = {
      threatTypes: computed(() => this.activePackage()?.tags?.threat_types ?? []),
      threatCategories: computed(() => this.activePackage()?.tags?.threat_categories ?? []),
      attackSurfaces: computed(() => this.activePackage()?.tags?.attack_surfaces ?? []),
      targetOses: computed(() => this.activePackage()?.tags?.target_oses ?? []),
    };
    getTitle = (id: string) => computed(() => this.huntPackageById(id)()?.title ?? 'Untitled');
    getAuthor = (id: string) => computed(() => this.huntPackageById(id)()?.author ?? 'Unknown');
    getStatus = (id: string) => computed(() => this.huntPackageById(id)()?.status ?? 'Unknown');
    getCompletedDate = (id: string) => computed(() => this.huntPackageById(id)()?.completed_date ?? null);
    getComplexityScore = (id: string) => computed(() => this.huntPackageById(id)()?.complexity?.score ?? 0);
    getResearchEffort = (id: string) => computed(() => this.huntPackageById(id)()?.complexity?.research ?? 0);
    getEnrichmentLevel = (id: string) => computed(() => this.huntPackageById(id)()?.complexity?.enrichment ?? 0);
    getReferenceLinks = (id: string) => computed(() => [
      ...(this.huntPackageById(id)()?.references?.blog_links ?? []),
      ...(this.huntPackageById(id)()?.references?.general ?? [])
    ]);
    getTagGoals = (id: string) => computed(() => this.huntPackageById(id)()?.tags?.goals ?? []);
    getTagTools = (id: string) => computed(() => this.huntPackageById(id)()?.tags?.tools ?? []);
    getTagActors = (id: string) => computed(() => this.huntPackageById(id)()?.tags?.actors ?? []);
    getQueryCondition = (id: string) => computed(() => this.huntPackageById(id)()?.query_logic?.condition ?? '');
    getQueryTables = (id: string) => computed(() => this.huntPackageById(id)()?.query_logic?.table ?? []);
    getRunbook = (id: string) => computed(() => this.huntPackageById(id)()?.response_actions?.analyst_runbook ?? '');
    getSeverity = (id: string) => computed(() => this.huntPackageById(id)()?.severity ?? 'Unknown');
    getSigma = (id: string) => computed(() => this.huntPackageById(id)()?.sigma ?? '');
  
    readonly complexityScore = computed(() => this.activePackage()?.complexity?.score ?? 0);
    
    // environments + tools + queryies
    readonly toolCategories = computed(() => {
      const tools = this.getToolsBySelectedEnvironment();
      const categories = tools.flatMap(tool =>
        tool.generatedContent.Hunt.map(mapping => mapping.category)
      );
      return Array.from(new Set(categories));
      });
    
    
    
      readonly mitigationRecommendations = computed(() => this.activePackage()?.response_actions.mitigation_recommendations ?? 'N/A');
    
      readonly selectedCategory = signal<string | null>(null);
    
      readonly filteredTools = computed(() => {
        const selected = this.selectedCategory();
        if (!selected) return this.getAllTools();
        return this.getAllTools().filter(tool =>
          tool.generatedContent.Hunt.some(mapping => mapping.category === selected)
        );
      });
    

    // TESTING REMOVE
    updateName(name: string) {
        console.log('updating name', name)
        const current = this._hunt();
        if (!current) return;
        this._hunt.set({ ...current, name });
    }

    updateExecutiveSummary(executiveSummary: string) {
        console.log('updating exec', executiveSummary)
        const current = this._hunt();
        if (!current) return;
        this._hunt.set({ ...current, executiveSummary });
    }

    updateOutcome(outcome: Enums.OutcomeEnum) {
        console.log('updating outcome', outcome)
        const current = this._hunt();
        if (!current) return;
        this._hunt.set({ ...current, outcome });
    }

    updateHypothesis(hypothesis: string) {
        console.log('updating hypothesis', hypothesis)
        const current = this._hunt();
        if (!current) return;
        this._hunt.set({ ...current, hypothesis });
    }
    updateAbstract(abstract: string) {
        console.log('updating abstract', abstract)
        const current = this._hunt();
        if (!current) return;
        this._hunt.set({ ...current, abstract });
    }

    updateNotes(notes: string) {
        console.log('updating notes', notes)
        const current = this._hunt();
        if (!current) return;
        this._hunt.set({ ...current, notes });
    }

    updateStatus(status: Enums.StatusEnum) {
        console.log('updating status', status)
        const current = this._hunt();
        if (!current) return;
        this._hunt.set({ ...current, status });
    }

    updateEditing(editing: boolean) {
        console.log('updating editing', editing)
        const current = this._hunt();
        if (!current) return;
        this._hunt.set({ ...current, editing });
    }

    updateOutcomeDependent(outcomeDependent: string) {
        const current = this._hunt();
        if (!current) return;
        this._hunt.set({ ...current, outcomeDependent });
    }

    load(hunt: Hunt): void {
        this._original.set(hunt);
        const cloned = structuredClone(hunt);
        this._hunt.set(cloned);

        const draft = localStorage.getItem(`hunt-draft-${hunt.id}`);
        if (draft) {
        try {
            this._hunt.set(JSON.parse(draft));
        } catch {
            console.warn('⚠️ Invalid draft format');
        }
        }

        this._undoStack.set([]);
        this._redoStack.set([]);
        this.draftSavedAt.set(null);
    }

    patch(patch: Partial<Hunt>): void {
        const current = this._hunt();
        if (!current) return;

        this.pushUndo();
        const updated = { ...current, ...patch };
        this._hunt.set(updated);
    }

    patchPackageById(packageId: string, update: DeepPartial<Hunt['huntPackages'][number]>): void {
        const current = this._hunt();
        if (!current) return;
    
        this.pushUndo();
        const updatedPackages = current.huntPackages.map(pkg => {
            if (pkg.UUID !== packageId) return pkg;
            
            // Deep merge the update with the existing package
            return deepMerge(pkg, update);
        });
    
        this._hunt.set({ ...current, huntPackages: updatedPackages });
    }

    commitToState(): void {
        const hunt = this._hunt();
        if (!hunt) return;
      
        this.huntStateService.updateHunt(structuredClone(hunt)); // ✅ use the injected one

    }
      

    appendPivotsToQueries(
      packageId: string,
      environmentId: string,
      toolId: string,
      queryIndex: number,
      newPivots: GeneratedContent['pivots']
    ): void {
      const hunt = this._hunt();
      if (!hunt) return;
    
      const updatedPackages = hunt.huntPackages.map(pkg => {
        if (pkg.UUID !== packageId) return pkg;
    
        const updatedAnalysis = pkg.analysis.map(env => {
          if (env.id !== environmentId) return env;
    
          const updatedTools = env.tools.map(tool => {
            if (tool.id !== toolId) return tool;
    
            const huntQueries = tool.generatedContent?.Hunt ? [...tool.generatedContent.Hunt] : [];
            const targetQuery = huntQueries[queryIndex];
    
            if (!targetQuery) {
              console.warn(`❌ No Hunt query at index ${queryIndex}`);
              return tool;
            }
    
            const existingPivots = targetQuery.pivots ?? [];
            const mergedPivots = [...existingPivots, ...(newPivots ?? [])];
    
            // Preserve the rest of the Hunt query properties
            const updatedHuntQueries = huntQueries.map((query, index) =>
              index === queryIndex ? { ...query, pivots: mergedPivots } : query
            );
    
            return {
              ...tool,
              generatedContent: {
                ...tool.generatedContent,
                Hunt: updatedHuntQueries
              }
            };
          });
    
          return {
            ...env,
            tools: updatedTools
          };
        });
    
        return {
          ...pkg,
          analysis: updatedAnalysis
        };
      });
    
      this.pushUndo();
      this._hunt.set({ ...hunt, huntPackages: updatedPackages });

      // ✅ Auto-rehydrate
      this.commitToState();


    }
    
                  

    reset(): void {
        const original = this._original();
        if (original) {
        this._hunt.set(structuredClone(original));
        this._undoStack.set([]);
        this._redoStack.set([]);
        this._savingStatus.set('idle');
        this.clearDraft();
        }
    }

    async submit(): Promise<void> {
        const current = this._hunt();
        if (!current) return;

        try {
        this._savingStatus.set('saving');
        // OR - just FINALIZE THE HUNT
        const saved = await this.huntService.updateHunt(current.id, current).toPromise();
        this._original.set(saved);
        this._hunt.set(structuredClone(saved));
        this._savingStatus.set('success');
        this.clearDraft();
        this._undoStack.set([]);
        this._redoStack.set([]);
        } catch (err) {
        this._savingStatus.set('error');
        console.error('❌ Failed to save hunt:', err);
        }
    }

    undo(): void {
        const stack = this._undoStack();
        if (!stack.length) return;

        const prev = stack[stack.length - 1];
        this._redoStack.set([...(this._redoStack()), this._hunt()!]);
        this._hunt.set(prev);
        this._undoStack.set(stack.slice(0, -1));
    }

    redo(): void {
        const stack = this._redoStack();
        if (!stack.length) return;

        const next = stack[stack.length - 1];
        this._undoStack.set([...(this._undoStack()), this._hunt()!]);
        this._hunt.set(next);
        this._redoStack.set(stack.slice(0, -1));
    }

    private pushUndo(): void {
        const current = this._hunt();
        if (current) {
        this._undoStack.set([...this._undoStack(), structuredClone(current)]);
        }
    }

    private clearDraft(): void {
        const id = this._hunt()?.id;
        if (id) {
        localStorage.removeItem(`hunt-draft-${id}`);
        this.draftSavedAt.set(null);
        }
    }

    updateSelectedQueryContent(
      {
        notes,
        query,
      }: {
        notes?: string;
        query?: string;
      },
      queryLogic: {
        selectedTool: () => any;
        selectedQueryIndex: () => number | null;
        isPivotQuery: () => boolean;
        selectedPivotIndex?: () => number | null;
      }
    ): void {
      const hunt = this._hunt();
      if (!hunt) return;
    
      const activePackage = this.activePackage();
      if (!activePackage) {
        console.warn('❗ No active package selected.');
        return;
      }
    
      const selectedTool = queryLogic.selectedTool();
      const selectedQueryIndex = queryLogic.selectedQueryIndex();
      const isPivot = queryLogic.isPivotQuery();
      const pivotIndex = queryLogic.selectedPivotIndex?.();
    
      if (!selectedTool?.id || selectedQueryIndex === null) {
        console.warn('❗ No selected tool/query index.');
        return;
      }
    
      const updatedPackages = hunt.huntPackages.map(pkg => {
        if (pkg.UUID !== activePackage.UUID) return pkg;
    
        const updatedAnalysis = pkg.analysis.map(env => {
          const updatedTools = env.tools.map(tool => {
            if (tool.id !== selectedTool.id) return tool;
    
            const huntQueries = tool.generatedContent?.Hunt ? [...tool.generatedContent.Hunt] : [];
            const baseQuery = huntQueries[selectedQueryIndex];
            if (!baseQuery) {
              console.warn('❗ No base Hunt query at index:', selectedQueryIndex);
              return tool;
            }
    
            // 🌀 Pivot Editing
            if (isPivot && pivotIndex !== undefined && baseQuery.pivots) {
              const updatedPivots = [...baseQuery.pivots];
              const pivotQuery = updatedPivots[pivotIndex];
              if (!pivotQuery) {
                console.warn('❗ No pivot query at pivot index:', pivotIndex);
                return tool;
              }
    
              if (notes !== undefined) pivotQuery.notes = notes;
              if (query !== undefined) pivotQuery.query = query;
    
              const updatedHuntQueries = huntQueries.map((q, i) =>
                i === selectedQueryIndex ? { ...q, pivots: updatedPivots } : q
              );
    
              return {
                ...tool,
                generatedContent: {
                  ...tool.generatedContent,
                  Hunt: updatedHuntQueries,
                },
              };
            }
    
            // 🧱 Base Query Editing
            if (notes !== undefined) baseQuery.notes = notes;
            if (query !== undefined) baseQuery.query = query;
    
            const updatedHuntQueries = huntQueries.map((q, i) =>
              i === selectedQueryIndex ? baseQuery : q
            );
    
            return {
              ...tool,
              generatedContent: {
                ...tool.generatedContent,
                Hunt: updatedHuntQueries,
              },
            };
          });
    
          return {
            ...env,
            tools: updatedTools,
          };
        });
    
        return {
          ...pkg,
          analysis: updatedAnalysis,
        };
      });
    
      this.pushUndo();
      this._hunt.set({ ...hunt, huntPackages: updatedPackages });
    
      console.log('✅ Saved updated query/pivot content');
    }
        }
